<template>
  <div v-if="show" class="modal-overlay" @click="$emit('close')">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <div class="header-content">
          <h2>
            <i class="fas fa-magic"></i>
            Review Certificate Assignment
          </h2>
          <p>
            Confirm which existing {{ entityTerm.plural.toLowerCase() }} this
            certificate belongs to. New records are created only when you opt in.
          </p>
        </div>
        <button @click="$emit('close')" class="modal-close">&times;</button>
      </div>
      
      <div class="modal-body split-pane">
        <!-- Left Panel: Document Preview -->
        <div class="document-panel">
          <div class="panel-header">
            <h4><i class="fas fa-file-pdf"></i> Document</h4>
            <p>{{ documentName }}</p>
          </div>
          
          <div class="document-viewer">
            <div v-if="safeDocumentUrl" class="pdf-container">
              <embed 
                :src="safeDocumentUrl" 
                type="application/pdf"
                class="pdf-embed">
              <div class="pdf-fallback">
                <a :href="safeDocumentUrl" target="_blank" class="btn btn-outline">
                  <i class="fas fa-external-link-alt"></i> Open in New Tab
                </a>
              </div>
            </div>
            <div v-else class="no-preview">
              <i class="fas fa-file-alt"></i>
              <p>Preview not available</p>
            </div>
          </div>
        </div>
        
        <!-- Right Panel: Entity Review -->
        <div class="entities-panel">
          <div class="panel-header">
            <h4><i class="fas fa-users"></i> Matched and extracted {{ entityTerm.plural }}</h4>
            <p>Matched records are selected. Unmatched names are left out by default.</p>
          </div>
          
          <div class="entities-list">
            <div v-for="(entity, index) in extractedEntities" :key="index" class="entity-item" :class="{'is-new': !entity.matched_entity_id, 'is-matched': entity.matched_entity_id}">
              <div class="entity-header">
                <div class="entity-icon" :class="getMatchTypeClass(entity.match_type)">
                  <i :class="getMatchTypeIcon(entity.match_type)"></i>
                </div>
                <div class="entity-info">
                  <strong>{{ entity.name }}</strong>
                  <span class="role-badge">{{ entity.role }}</span>
                </div>
              </div>
              
              <!-- Match Status - CLEAR AND OBVIOUS -->
              <div class="match-status" :class="getMatchTypeClass(entity.match_type)">
                <div v-if="entity.matched_entity_id" class="match-exact">
                  <div class="status-badge status-matched">
                    <i class="fas fa-check-circle"></i>
                    <strong>EXISTING {{ roleTerm(entity).toUpperCase() }}</strong>
                  </div>
                  <p class="status-message">
                    Certificate will attach to:
                    <strong>{{ entity.matched_entity_name }}</strong>
                  </p>
                  <p class="status-detail">
                    {{ formatMatchType(entity.match_type) }} match — no new record will be created
                  </p>
                </div>
                <div v-else class="match-new">
                  <div class="status-badge status-new">
                    <i class="fas fa-question-circle"></i>
                    <strong>UNMATCHED</strong>
                  </div>
                  <p class="status-message">
                    <strong>{{ entity.name }}</strong> was found on the certificate.
                  </p>
                  <p class="status-detail">
                    It will be ignored unless you explicitly create a new {{ roleTerm(entity) }}.
                  </p>
                </div>
              </div>
              
              <!-- Entity Details (Editable) -->
              <div class="entity-details">
                <div class="form-group">
                  <label>Company Name</label>
                  <input 
                    v-model="entity.name" 
                    class="form-control"
                    :placeholder="`${entityTerm.singular} name`">
                </div>
                
                <div v-if="entity.address" class="form-group">
                  <label>Address</label>
                  <input 
                    v-model="entity.address" 
                    class="form-control"
                    placeholder="Address">
                </div>
                
                <!-- Agent Contacts (for AGENT role) -->
                <div v-if="entity.role === 'AGENT' && entity.producer_contacts && entity.producer_contacts.length > 0" class="agent-contacts-section">
                  <h5 class="contacts-header">
                    <i class="fas fa-user-tie"></i>
                    Agent Contact{{ entity.producer_contacts.length > 1 ? 's' : '' }}
                  </h5>
                  <div v-for="(contact, contactIndex) in entity.producer_contacts" :key="contactIndex" class="contact-item">
                    <div class="form-group">
                      <label>Contact Name</label>
                      <input 
                        v-model="contact.name" 
                        class="form-control"
                        placeholder="Agent name">
                    </div>
                    <div class="form-group">
                      <label>Email</label>
                      <input 
                        v-model="contact.email" 
                        type="email"
                        class="form-control"
                        placeholder="email@example.com">
                    </div>
                    <div class="form-group">
                      <label>Phone</label>
                      <input 
                        v-model="contact.phone" 
                        class="form-control"
                        placeholder="(555) 123-4567">
                    </div>
                  </div>
                </div>
                
                <!-- Store Numbers (for INSURED role) - EDITABLE -->
                <div
                  v-if="entity.role === 'INSURED' && matchStrategy === 'store_code' && entity.create_new"
                  class="store-numbers-section">
                  <h5 class="stores-header">
                    <i class="fas fa-map-marker-alt"></i>
                    Store Numbers
                    <span v-if="entity.store_numbers && entity.store_numbers.length > 0" class="store-count-badge">
                      {{ entity.store_numbers.length }}
                    </span>
                  </h5>
                  
                  <!-- Editable Store Number List -->
                  <div class="store-numbers-editable">
                    <div v-for="(storeNum, idx) in entity.store_numbers || []" :key="idx" class="store-number-item">
                      <input 
                        v-model="entity.store_numbers[idx]" 
                        class="store-input"
                        placeholder="Store #">
                      <button 
                        @click="removeStoreNumber(entity, idx)" 
                        class="btn-remove-store"
                        title="Remove store number">
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                    
                    <!-- Add New Store Number -->
                    <div class="add-store-row">
                      <input 
                        v-model="newStoreNumbers[entity.name]" 
                        class="store-input add-store-input"
                        placeholder="Add store number (e.g., 7092 or 7092A)"
                        @keyup.enter="addStoreNumber(entity)">
                      <button 
                        @click="addStoreNumber(entity)" 
                        class="btn-add-store"
                        :disabled="!newStoreNumbers[entity.name]">
                        <i class="fas fa-plus"></i> Add
                      </button>
                    </div>
                  </div>
                  
                  <p v-if="entity.store_numbers && entity.store_numbers.length > 0" class="store-hint">
                    <i class="fas fa-info-circle"></i>
                    {{ locationTerm.plural }} will be created for these codes
                  </p>
                  <p v-else class="store-hint store-hint-empty">
                    <i class="fas fa-exclamation-triangle"></i>
                    No store numbers detected. Add them manually if needed.
                  </p>
                </div>
              </div>
              
              <!-- Actions -->
              <div class="entity-actions">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    v-model="entity.include"
                    @change="handleIncludeChange(entity)">
                  <span>
                    {{ entity.matched_entity_id ? 'Attach and analyze' : `Create new ${roleTerm(entity)}` }}
                  </span>
                </label>
                
                <button 
                  v-if="entity.matched_entity_id" 
                  @click="createAsNew(entity)"
                  class="btn-link">
                  Create as new instead
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="$emit('close')" class="btn btn-secondary">
          Cancel
        </button>
        <button 
          @click="confirmEntities" 
          :disabled="confirming || !hasIncludedEntities"
          class="btn btn-primary">
          <i v-if="confirming" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-check"></i>
          {{ confirming ? 'Starting analysis...' : `Attach and analyze ${includedCount}` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'EntityExtractionModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    projectId: {
      type: [String, Number],
      required: true
    },
    documentId: {
      type: [String, Number],
      default: null
    },
    documentFile: {
      type: File,
      default: null
    },
    documentName: {
      type: String,
      default: 'Document'
    },
    documentUrl: {
      type: String,
      default: null
    },
    extractedEntities: {
      type: Array,
      default: () => []
    },
    entityTerm: {
      type: Object,
      default: () => ({ singular: 'Company', plural: 'Companies' })
    },
    locationTerm: {
      type: Object,
      default: () => ({ singular: 'Location', plural: 'Locations' })
    },
    matchStrategy: {
      type: String,
      default: 'entity_name'
    },
    subjectId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      confirming: false,
      newStoreNumbers: {}  // Track new store number inputs per entity
    };
  },
  computed: {
    hasIncludedEntities() {
      return this.extractedEntities.some(e => e.include !== false);
    },
    includedCount() {
      return this.extractedEntities.filter(e => e.include !== false).length;
    },
    // Validate documentUrl before embedding: reject broken S3 URLs that would cause
    // a DNS failure (e.g. "https://.s3.amazonaws.com/..." when the bucket name is
    // empty due to misconfigured IS_PRODUCTION detection). Only accept http(s) URLs
    // that are NOT pointing to an S3 hostname with an empty bucket prefix.
    safeDocumentUrl() {
      const url = this.documentUrl;
      if (!url || typeof url !== 'string') return null;
      if (!url.startsWith('http://') && !url.startsWith('https://')) return null;
      // Reject URLs where the bucket name is empty: ".s3.amazonaws.com"
      if (/https?:\/\/\.s3\.amazonaws\.com/.test(url)) return null;
      return url;
    }
  },
  methods: {
    // Producers are insurance agencies, never the project's compliance term
    // (subcontractor, franchisee, ...), so label their rows separately.
    roleTerm(entity) {
      return entity.role === 'AGENT' ? 'agent' : this.entityTerm.singular.toLowerCase();
    },

    getMatchTypeClass(matchType) {
      if (matchType === 'store_number' || matchType === 'store_number_variant') return 'match-exact';
      if (matchType === 'location_address') return 'match-exact';
      if (matchType === 'exact') return 'match-exact';
      if (matchType === 'fuzzy') return 'match-fuzzy';
      if (matchType === 'ai_confirmed') return 'match-ai';
      if (matchType === 'will_create') return 'match-new';
      return 'match-new';
    },
    
    getMatchTypeIcon(matchType) {
      if (matchType === 'exact') return 'fas fa-check-circle';
      if (matchType === 'fuzzy') return 'fas fa-link';
      if (matchType === 'ai_confirmed') return 'fas fa-robot';
      if (matchType === 'will_create') return 'fas fa-plus-circle';
      return 'fas fa-plus-circle';
    },
    
    createAsNew(entity) {
      entity.matched_entity_id = null;
      entity.matched_subject_id = null;
      entity.match_type = 'created';
      entity.create_new = true;
      entity.include = true;
    },

    handleIncludeChange(entity) {
      if (!entity.matched_entity_id) {
        entity.create_new = entity.include;
      }
    },

    formatMatchType(matchType) {
      const labels = {
        store_number: 'Location code',
        store_number_variant: 'Location code variant',
        location_address: 'Site address',
        exact: 'Exact name',
        fuzzy: 'Similar name',
        ai_confirmed: 'AI-confirmed name'
      };
      return labels[matchType] || 'Existing record';
    },
    
    addStoreNumber(entity) {
      const newNumber = this.newStoreNumbers[entity.name];
      if (!newNumber || !newNumber.trim()) return;
      
      // Clean the input - remove # prefix if present, uppercase letters
      let cleanNumber = newNumber.trim().replace(/^#/, '').toUpperCase();
      
      // Initialize store_numbers array if not present
      if (!entity.store_numbers) {
        entity.store_numbers = [];
      }
      
      // Check for duplicates
      if (entity.store_numbers.includes(cleanNumber)) {
        alert(`Store number ${cleanNumber} is already in the list.`);
        return;
      }
      
      entity.store_numbers.push(cleanNumber);
      this.newStoreNumbers[entity.name] = '';  // Clear input
    },
    
    removeStoreNumber(entity, index) {
      if (entity.store_numbers && index >= 0 && index < entity.store_numbers.length) {
        entity.store_numbers.splice(index, 1);
      }
    },
    
    async confirmEntities() {
      this.confirming = true;
      
      try {
        const entitiesToConfirm = this.extractedEntities.filter(e => e.include !== false);
        
        const payload = {
          document_id: this.documentId,
          entities: entitiesToConfirm,
        };
        // Forward the originating subject_id so the backend can guarantee the
        // check runs for the pre-selected entity even if COI extraction named
        // a different or no insured entity.
        if (this.subjectId) payload.subject_id = this.subjectId;

        const response = await api.post(`/projects/${this.projectId}/confirm-entities/`, payload);
        
        this.$emit('entities-confirmed', response.data);
        this.$emit('close');  // Close modal after successful confirmation
      } catch (error) {
        console.error('Error confirming entities:', error);
        alert('Failed to confirm entities. Please try again.');
      } finally {
        this.confirming = false;
      }
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 2rem;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 95%;
  max-width: 1400px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 1.5rem 2rem;
  border-bottom: 2px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
}

.header-content h2 {
  margin: 0;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-content p {
  margin: 0.5rem 0 0 0;
  opacity: 0.9;
  font-size: 0.9375rem;
}

.modal-close {
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s ease;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-body {
  flex: 1;
  overflow: hidden;
}

.split-pane {
  display: grid;
  grid-template-columns: 1fr 1fr;
  height: 100%;
  gap: 0;
}

.document-panel,
.entities-panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.document-panel {
  border-right: 2px solid #e2e8f0;
}

.panel-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.panel-header h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  color: #1a202c;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.panel-header p {
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
}

.document-viewer {
  flex: 1;
  overflow: hidden;
  padding: 1rem;
}

.pdf-container {
  height: 100%;
  position: relative;
}

.pdf-embed {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
}

.pdf-fallback {
  margin-top: 1rem;
  text-align: center;
}

.no-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #cbd5e0;
}

.no-preview i {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.entities-list {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.entity-item {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  transition: all 0.2s;
}

.entity-item.is-matched {
  border-color: #10b981;
  background: #f0fdf4;
}

.entity-item.is-new {
  border-color: #f59e0b;
  background: #fffbeb;
}

.entity-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.entity-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.entity-icon.match-exact {
  background: #d1fae5;
  color: #065f46;
}

.entity-icon.match-fuzzy {
  background: #dbeafe;
  color: #1e40af;
}

.entity-icon.match-ai {
  background: #e0e7ff;
  color: #5b21b6;
}

.entity-icon.match-new {
  background: #fef3c7;
  color: #92400e;
}

.entity-info {
  flex: 1;
}

.entity-info strong {
  display: block;
  font-size: 1.125rem;
  color: #1a202c;
  margin-bottom: 0.25rem;
}

.role-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 12px;
  font-size: 0.8125rem;
  font-weight: 500;
}

.match-status {
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.9375rem;
}

.match-status.match-exact {
  background: #d1fae5;
  color: #065f46;
}

.match-status.match-fuzzy {
  background: #dbeafe;
  color: #1e40af;
}

.match-status.match-ai {
  background: #e0e7ff;
  color: #5b21b6;
}

.match-status.match-new {
  background: #fef3c7;
  color: #92400e;
}

/* STATUS BADGES - SUPER OBVIOUS */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.75rem;
}

.status-badge.status-matched {
  background: #10b981;
  color: white;
}

.status-badge.status-new {
  background: #f59e0b;
  color: white;
}

.status-message {
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0.5rem 0;
}

.status-detail {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0.25rem 0 0 0;
  font-style: italic;
}

.match-status i {
  margin-right: 0.5rem;
}

.entity-details {
  margin-bottom: 1rem;
}

.agent-contacts-section,
.store-numbers-section {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.contacts-header,
.stores-header {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #475569;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.contact-item {
  padding: 1rem;
  background: white;
  border-radius: 6px;
  margin-bottom: 1rem;
  border: 1px solid #e2e8f0;
}

.contact-item:last-child {
  margin-bottom: 0;
}

.store-numbers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.store-badge {
  display: inline-block;
  padding: 0.5rem 1rem;
  background: #667eea;
  color: white;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.875rem;
}

.store-hint {
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0.75rem 0 0 0;
  font-style: italic;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.store-hint-empty {
  color: #f59e0b;
}

.store-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.375rem;
  background: #667eea;
  color: white;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.5rem;
}

/* Editable Store Numbers */
.store-numbers-editable {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.store-number-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.store-input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9375rem;
  font-weight: 500;
}

.store-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.btn-remove-store {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: #fee2e2;
  color: #dc2626;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-remove-store:hover {
  background: #fecaca;
  color: #b91c1c;
}

.add-store-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #e2e8f0;
}

.add-store-input {
  flex: 1;
}

.btn-add-store {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-add-store:hover:not(:disabled) {
  background: #059669;
}

.btn-add-store:disabled {
  background: #d1d5db;
  cursor: not-allowed;
}

.form-group {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  margin-bottom: 0.375rem;
}

.form-control {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9375rem;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.entity-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9375rem;
}

.btn-link {
  background: none;
  border: none;
  color: #667eea;
  font-size: 0.875rem;
  cursor: pointer;
  text-decoration: underline;
}

.btn-link:hover {
  color: #5a67d8;
}

.modal-footer {
  padding: 1.5rem 2rem;
  border-top: 2px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  background: #f8fafc;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5a67d8;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #f8fafc;
}

.btn-outline {
  background: white;
  color: #667eea;
  border: 1px solid #667eea;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.btn-outline:hover {
  background: #667eea;
  color: white;
}

@media (max-width: 1024px) {
  .split-pane {
    grid-template-columns: 1fr;
  }
  
  .document-panel {
    border-right: none;
    border-bottom: 2px solid #e2e8f0;
    max-height: 400px;
  }
  
  .entity-details {
    grid-template-columns: 1fr;
  }
}
</style>

